/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The tenant's Real Estate country variant (UAE or Australia), for the RE
 * screens.
 *
 * The backend owns the packs (backend/app/real_estate/variants/) and sends the
 * tenant's one as `re_variant` on /module-entitlements; useModuleEntitlements
 * hands it to setReVariant. A tenant has one variant for the whole session, so
 * this is a module-level value the formatters in screens/api.js can read
 * without every caller threading it through. Until it arrives (or on a backend
 * that predates variants) the UAE values apply, which is how every screen
 * behaved before.
 *
 * Kept free of imports so it can be unit tested without loading auth/Auth.
 */

// The parts of backend/app/real_estate/variants/uae.py the screens use.
export const UAE_PACK = {
  code: 'UAE',
  name: 'United Arab Emirates',
  currency: 'AED',
  locale: 'en-AE',
  region_label: 'Emirate',
  regions: [
    { code: 'DUBAI', label: 'Dubai' },
    { code: 'ABU_DHABI', label: 'Abu Dhabi' },
    { code: 'SHARJAH', label: 'Sharjah' },
    { code: 'AJMAN', label: 'Ajman' },
    { code: 'RAS_AL_KHAIMAH', label: 'Ras Al Khaimah' },
    { code: 'UMM_AL_QUWAIN', label: 'Umm Al Quwain' },
    { code: 'FUJAIRAH', label: 'Fujairah' },
  ],
  tax_name: 'VAT',
  tax_pct: 5,
  tax_registration_label: 'TRN',
  booking_flow: [
    { code: 'DRAFT', label: 'Draft' },
    { code: 'RESERVED', label: 'Reserved' },
    { code: 'SPA_ISSUED', label: 'SPA issued' },
    { code: 'SPA_SIGNED', label: 'SPA signed' },
    { code: 'REGISTERED', label: 'Registered (Oqood)' },
    { code: 'HANDED_OVER', label: 'Handed over' },
  ],
  booking_exit_statuses: ['CANCELLED'],
  booking_final_statuses: ['CANCELLED', 'HANDED_OVER'],
  listing_portals: [
    { code: 'BAYUT', label: 'Bayut' },
    { code: 'PROPERTY_FINDER', label: 'Property Finder' },
    { code: 'DUBIZZLE', label: 'Dubizzle' },
  ],
  buyer_categories: [
    { code: 'UAE_NATIONAL', label: 'UAE national' },
    { code: 'RESIDENT', label: 'Resident' },
    { code: 'NON_RESIDENT', label: 'Non-resident' },
  ],
  rent_frequencies: [
    { code: 'ANNUAL', label: 'Annual (1 cheque)' },
    { code: 'SEMI_ANNUAL', label: 'Semi-annual (2 cheques)' },
    { code: 'QUARTERLY', label: 'Quarterly (4 cheques)' },
    { code: 'MONTHLY', label: 'Monthly (12 cheques)' },
  ],
  rent_payment_model: 'CHEQUES',
  rent_payment_method: 'CHEQUE',
  mortgage_rules: { ratio: 'LTV', debt_ratio: 'DBR' },
  fee_rules: { registration_fee_name: 'DLD fee', has_oqood: true },
  leasing_rules: { registration_name: 'Ejari' },
  hidden_fields: {
    re_projects: ['state', 'postcode', 'council', 'strata_plan_no'],
    re_units: ['strata_levy_quarterly', 'council_rates_annual', 'land_size_sqm'],
    re_bookings: [
      'contract_exchanged_date', 'cooling_off_ends', 'deposit_pct', 'deposit_held_by',
      'finance_approval_date', 'settlement_date', 'sunset_date', 'transfer_duty_estimate', 'firb_required',
    ],
    re_listings: ['sale_method', 'auction_at', 'inspection_times', 'agency_agreement_type', 'agent_licence_no'],
    re_leases: ['bond_amount', 'bond_authority', 'bond_lodgement_no', 'condition_report_at'],
    re_mortgage_applications: ['lmi_required', 'buyer_category', 'credit_licence_no'],
    re_broker_firms: ['abn', 'acn', 'state_licence_no'],
  },
  labels: {
    buyers_and_tenants: 'Buyers and tenants',
    vendors_and_landlords: 'Vendors and landlords',
    viewing: 'Viewing',
    booking: 'Booking',
    spa: 'SPA',
    national_id: 'Emirates ID',
    developer_registration: 'RERA developer no.',
    broker_registration: 'RERA ORN',
    listing_permit: 'Trakheesi permit',
  },
};

let current = UAE_PACK;
const listeners = new Set();

/** Set from the /module-entitlements payload. Null or empty keeps UAE. */
export function setReVariant(pack) {
  current = pack && pack.code ? { ...UAE_PACK, ...pack } : UAE_PACK;
  listeners.forEach((fn) => fn(current));
}

export function getReVariant() {
  return current;
}

/** Subscribe to changes (useReVariant). Returns the unsubscribe function. */
export function onReVariantChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const isAustralia = (pack = current) => pack.code === 'AU';
export const usesCheques = (pack = current) => pack.rent_payment_model === 'CHEQUES';
export const defaultCurrency = (pack = current) => pack.currency || 'AED';
export const reLocale = (pack = current) => pack.locale || 'en-AE';

/** Whether a column does not apply in this country (and is not shown). */
export function isHidden(table, column, pack = current) {
  return Boolean((pack.hidden_fields?.[table] || []).includes(column));
}

/** This country's word for something; `fallback` when the pack has none. */
export function reLabel(key, fallback, pack = current) {
  return pack.labels?.[key] || fallback;
}

/** Booking statuses in order, without the exit statuses. */
export function bookingFlowCodes(pack = current) {
  return (pack.booking_flow || []).map((c) => c.code);
}

/** Every booking status this country uses, the exits (CANCELLED) last. */
export function bookingStatusCodes(pack = current) {
  return [...bookingFlowCodes(pack), ...(pack.booking_exit_statuses || [])];
}

/** {code: label} for the booking flow. */
export function bookingStatusLabels(pack = current) {
  return Object.fromEntries((pack.booking_flow || []).map((c) => [c.code, c.label]));
}

export const choiceOptions = (choices) => (choices || []).map((c) => ({ value: c.code, label: c.label }));
export const regionOptions = (pack = current) => choiceOptions(pack.regions);
export const portalOptions = (pack = current) => choiceOptions(pack.listing_portals);
export const buyerCategoryOptions = (pack = current) => choiceOptions(pack.buyer_categories);
export const rentFrequencyOptions = (pack = current) => choiceOptions(pack.rent_frequencies);
